export const CANDIDATE_WRITE_FAILURE_SETTING_KEYS = {
  retryDelayMs: "candidate_write_retry_delay_ms",
  notificationEnabled: "candidate_write_failure_notification_enabled",
  notificationMessage: "candidate_write_failure_notification_message",
  contactEmail: "candidate_write_failure_contact_email",
} as const;

export interface CandidateWriteFailureSettings {
  retryDelayMs: number;
  notificationEnabled: boolean;
  notificationMessage: string;
  contactEmail: string;
}

export const DEFAULT_CANDIDATE_WRITE_FAILURE_SETTINGS:
CandidateWriteFailureSettings = {
  retryDelayMs: 500,
  notificationEnabled: false,
  notificationMessage:
    "We could not save your changes or report the problem automatically. Please contact support.",
  contactEmail: "",
};

export interface NormalizedCandidateWriteError {
  message: string;
  status?: number;
}

export interface CandidateWriteFailureIdentity {
  name: string;
  emailAddress: string;
  profileId: string;
}

export interface CandidateWriteFailureEmailDetails {
  identity: CandidateWriteFailureIdentity;
  operation: string;
  targetPath: string;
  initialError: NormalizedCandidateWriteError;
  retryError: NormalizedCandidateWriteError;
  telemetryError: NormalizedCandidateWriteError;
  networkOnline?: boolean;
  correlationId: string;
  occurredAt: string;
}

function hasSetting(settings: Record<string, unknown>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(settings, key);
}

function parseBoolean(value: unknown, fallback: boolean): boolean {
  if (value === true || value === "true") return true;
  if (value === false || value === "false") return false;
  return fallback;
}

export function parseCandidateWriteFailureSettings(
  settings: Record<string, unknown>,
): CandidateWriteFailureSettings {
  const retryRaw = settings[CANDIDATE_WRITE_FAILURE_SETTING_KEYS.retryDelayMs];
  const retryValue = typeof retryRaw === "string" && !retryRaw.trim()
    ? Number.NaN
    : Number(retryRaw);
  const retryDelayMs = Number.isFinite(retryValue) && retryValue >= 0
    ? Math.floor(retryValue)
    : DEFAULT_CANDIDATE_WRITE_FAILURE_SETTINGS.retryDelayMs;

  return {
    retryDelayMs,
    notificationEnabled: hasSetting(
      settings,
      CANDIDATE_WRITE_FAILURE_SETTING_KEYS.notificationEnabled,
    )
      ? parseBoolean(
          settings[
            CANDIDATE_WRITE_FAILURE_SETTING_KEYS.notificationEnabled
          ],
          DEFAULT_CANDIDATE_WRITE_FAILURE_SETTINGS.notificationEnabled,
        )
      : DEFAULT_CANDIDATE_WRITE_FAILURE_SETTINGS.notificationEnabled,
    notificationMessage: hasSetting(
      settings,
      CANDIDATE_WRITE_FAILURE_SETTING_KEYS.notificationMessage,
    )
      ? String(
          settings[
            CANDIDATE_WRITE_FAILURE_SETTING_KEYS.notificationMessage
          ] ?? "",
        )
      : DEFAULT_CANDIDATE_WRITE_FAILURE_SETTINGS.notificationMessage,
    contactEmail: hasSetting(
      settings,
      CANDIDATE_WRITE_FAILURE_SETTING_KEYS.contactEmail,
    )
      ? String(
          settings[CANDIDATE_WRITE_FAILURE_SETTING_KEYS.contactEmail] ?? "",
        ).trim()
      : DEFAULT_CANDIDATE_WRITE_FAILURE_SETTINGS.contactEmail,
  };
}

export function normalizeCandidateWriteError(
  error: unknown,
): NormalizedCandidateWriteError {
  if (error instanceof Error) {
    const candidate = error as Error & {
      status?: number;
      statusCode?: number;
      data?: { detail?: string; title?: string };
    };
    return {
      message:
        candidate.data?.detail
        || candidate.data?.title
        || candidate.message
        || "Unknown error",
      status: candidate.status ?? candidate.statusCode,
    };
  }

  if (typeof error === "string") {
    return { message: error };
  }

  return { message: "Unknown error" };
}

export function buildCandidateWriteFailureMailto(
  contactEmail: string,
  details: CandidateWriteFailureEmailDetails,
): string {
  const initialStatus = details.initialError.status
    ? ` (HTTP ${details.initialError.status})`
    : "";
  const retryStatus = details.retryError.status
    ? ` (HTTP ${details.retryError.status})`
    : "";
  const telemetryStatus = details.telemetryError.status
    ? ` (HTTP ${details.telemetryError.status})`
    : "";
  const networkStatus = details.networkOnline === false
    ? "Offline (browser-reported)"
    : details.networkOnline === true
      ? "Online (browser-reported; connectivity is not guaranteed)"
      : "Unavailable";
  const failureType = details.retryError.status
    ? "HTTP response received"
    : "No HTTP response (possible offline, DNS, TLS, CORS, proxy, or firewall failure)";
  const body = [
    "A candidate data write could not be completed.",
    "",
    `Name: ${details.identity.name}`,
    `Email: ${details.identity.emailAddress}`,
    `Profile ID: ${details.identity.profileId}`,
    `Operation: ${details.operation}`,
    `Target: ${details.targetPath}`,
    `Client network: ${networkStatus}`,
    `Failure type: ${failureType}`,
    `Initial write error: ${details.initialError.message}${initialStatus}`,
    `Retry write error: ${details.retryError.message}${retryStatus}`,
    `Telemetry error: ${details.telemetryError.message}${telemetryStatus}`,
    `Correlation ID: ${details.correlationId}`,
    `Occurred at: ${details.occurredAt}`,
  ].join("\n");
  const subject = `Candidate data write failure ${details.correlationId}`;
  const query = [
    `subject=${encodeURIComponent(subject)}`,
    `body=${encodeURIComponent(body)}`,
  ].join("&");

  return `mailto:${encodeURIComponent(contactEmail)}?${query}`;
}

export async function executeCandidateWriteWithRetry<T>(options: {
  write: () => Promise<T>;
  retryDelayMs: number;
  wait?: (delayMs: number) => Promise<void>;
  onExhausted: (initialError: unknown, retryError: unknown) => Promise<void>;
}): Promise<T> {
  try {
    return await options.write();
  } catch (initialError) {
    const wait = options.wait
      ?? ((delayMs: number) =>
        new Promise<void>((resolve) => setTimeout(resolve, delayMs)));
    await wait(options.retryDelayMs);

    try {
      return await options.write();
    } catch (retryError) {
      await options.onExhausted(initialError, retryError);
      throw retryError;
    }
  }
}

export async function reportCandidateWriteFailure(options: {
  notificationEnabled: boolean;
  writeTelemetry: () => Promise<void>;
  publishNotification: (telemetryError: unknown) => void;
}): Promise<{
  telemetryWritten: boolean;
  notificationPublished: boolean;
}> {
  try {
    await options.writeTelemetry();
    return {
      telemetryWritten: true,
      notificationPublished: false,
    };
  } catch (telemetryError) {
    if (options.notificationEnabled) {
      options.publishNotification(telemetryError);
    }
    return {
      telemetryWritten: false,
      notificationPublished: options.notificationEnabled,
    };
  }
}
